/**
 * NWS Hero Carousel — nws.js
 * Handles: slide transitions, arrow nav, dot nav,
 *          autoplay, touch/swipe, keyboard nav,
 *          pause on hover/focus, reduced-motion respect.
 */

(function () {
  'use strict';

  // -------------------------------------------------------
  // Constants
  // -------------------------------------------------------
  const SELECTOR_CAROUSEL   = '.nws-hero-carousel';
  const SELECTOR_TRACK      = '.nws-carousel__track';
  const SELECTOR_SLIDE      = '.nws-carousel__slide';
  const SELECTOR_ARROW_PREV = '.nws-carousel__arrow--prev';
  const SELECTOR_ARROW_NEXT = '.nws-carousel__arrow--next';
  const SELECTOR_DOT        = '.nws-carousel__dot';
  const CLASS_ACTIVE        = 'is-active';
  const SWIPE_THRESHOLD     = 50; // px minimum swipe distance

  // -------------------------------------------------------
  // Helper: prefix CSS custom property transition on track
  // -------------------------------------------------------
  function setTransitionSpeed(track, ms) {
    track.style.transition = `transform ${ms}ms cubic-bezier(0.4, 0, 0.2, 1)`;
  }

  // -------------------------------------------------------
  // NWSCarousel Class
  // -------------------------------------------------------
  class NWSCarousel {
    constructor(el) {
      this.root           = el;
      this.track          = el.querySelector(SELECTOR_TRACK);
      this.slides         = Array.from(el.querySelectorAll(SELECTOR_SLIDE));
      this.dots           = Array.from(el.querySelectorAll(SELECTOR_DOT));
      this.arrowPrev      = el.querySelector(SELECTOR_ARROW_PREV);
      this.arrowNext      = el.querySelector(SELECTOR_ARROW_NEXT);

      // Config from data attributes
      this.total          = this.slides.length;
      this.current        = 0;
      this.autoplay       = el.dataset.autoplay === 'true';
      this.autoplaySpeed  = (parseInt(el.dataset.autoplaySpeed, 10) || 5) * 1000;
      this.transitionSpeed= parseInt(el.dataset.transitionSpeed, 10) || 700;
      this.isPaused       = false;
      this.isAnimating    = false;

      // Touch state
      this.touchStartX    = 0;
      this.touchStartY    = 0;
      this.touchEndX      = 0;
      this.touchCurrentX  = 0;
      this.isTouching     = false;

      this._timer         = null;
      this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.total <= 1) return; // no point setting up nav for one slide

      this._init();
    }

    // -------------------------------------------------------
    // Initialise
    // -------------------------------------------------------
    _init() {
      // Set initial transition speed
      if (!this._reducedMotion) {
        setTransitionSpeed(this.track, this.transitionSpeed);
      }

      // Position track
      this._setTrackPosition(0, false);

      // Arrow listeners
      if (this.arrowPrev) {
        this.arrowPrev.addEventListener('click', () => this.prev());
      }
      if (this.arrowNext) {
        this.arrowNext.addEventListener('click', () => this.next());
      }

      // Dot listeners
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i));
      });

      // Keyboard navigation (whole carousel)
      this.root.addEventListener('keydown', (e) => this._onKeydown(e));
      this.root.setAttribute('tabindex', '0'); // make focusable for keyboard

      // Touch events
      this.track.addEventListener('touchstart',  (e) => this._onTouchStart(e),  { passive: true });
      this.track.addEventListener('touchmove',   (e) => this._onTouchMove(e),   { passive: false });
      this.track.addEventListener('touchend',    (e) => this._onTouchEnd(e),    { passive: true });
      this.track.addEventListener('touchcancel', (e) => this._onTouchCancel(e), { passive: true });

      // Pause autoplay on hover / focus
      this.root.addEventListener('mouseenter', () => this._pause());
      this.root.addEventListener('mouseleave', () => this._resume());
      this.root.addEventListener('focusin',    () => this._pause());
      this.root.addEventListener('focusout',   (e) => {
        if (!this.root.contains(e.relatedTarget)) this._resume();
      });

      // Visibility API — pause when tab hidden
      document.addEventListener('visibilitychange', () => {
        document.hidden ? this._pause() : this._resume();
      });

      // Reduced motion listener (dynamic)
      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
        this._reducedMotion = e.matches;
        if (this._reducedMotion) {
          this.track.style.transition = 'none';
          this._pause();
        } else {
          setTransitionSpeed(this.track, this.transitionSpeed);
          if (this.autoplay) this._resume();
        }
      });

      // Start autoplay
      if (this.autoplay && !this._reducedMotion) {
        this._startTimer();
      }
    }

    // -------------------------------------------------------
    // Navigate
    // -------------------------------------------------------
    goTo(index, skipAnimation) {
      if (this.isAnimating && !skipAnimation) return;
      if (index === this.current) return;

      const prev = this.current;
      this.current = ((index % this.total) + this.total) % this.total;

      // Update slides
      this.slides[prev].classList.remove(CLASS_ACTIVE);
      this.slides[prev].setAttribute('aria-hidden', 'true');
      this.slides[this.current].classList.add(CLASS_ACTIVE);
      this.slides[this.current].setAttribute('aria-hidden', 'false');

      // Update dots
      if (this.dots[prev])        this.dots[prev].classList.remove(CLASS_ACTIVE);
      if (this.dots[this.current]) {
        this.dots[this.current].classList.add(CLASS_ACTIVE);
        this.dots[prev].setAttribute('aria-selected', 'false');
        this.dots[this.current].setAttribute('aria-selected', 'true');
      }

      // Move track
      this._setTrackPosition(this.current, !skipAnimation && !this._reducedMotion);

      // Animate lock
      if (!skipAnimation && !this._reducedMotion) {
        this.isAnimating = true;
        const onEnd = () => {
          this.isAnimating = false;
          this.track.removeEventListener('transitionend', onEnd);
        };
        this.track.addEventListener('transitionend', onEnd);
      }

      // Restart timer
      if (this.autoplay && !this.isPaused) {
        this._clearTimer();
        this._startTimer();
      }
    }

    next() { this.goTo(this.current + 1); }
    prev() { this.goTo(this.current - 1); }

    // -------------------------------------------------------
    // Track positioning
    // -------------------------------------------------------
    _setTrackPosition(index, animate) {
      if (!animate) {
        this.track.style.transition = 'none';
      } else {
        setTransitionSpeed(this.track, this.transitionSpeed);
      }
      this.track.style.transform = `translateX(-${index * 100}%)`;
    }

    // -------------------------------------------------------
    // Autoplay timer
    // -------------------------------------------------------
    _startTimer() {
      this._clearTimer();
      this._timer = setInterval(() => this.next(), this.autoplaySpeed);
    }

    _clearTimer() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    }

    _pause() {
      this.isPaused = true;
      this._clearTimer();
    }

    _resume() {
      this.isPaused = false;
      if (this.autoplay && !this._reducedMotion) {
        this._startTimer();
      }
    }

    // -------------------------------------------------------
    // Touch / Swipe
    // -------------------------------------------------------
    _onTouchStart(e) {
      const touch = e.touches[0];
      this.touchStartX  = touch.clientX;
      this.touchStartY  = touch.clientY;
      this.touchCurrentX = touch.clientX;
      this.isTouching   = true;
      this._pause();
      // Remove transition for live drag feel
      this.track.style.transition = 'none';
    }

    _onTouchMove(e) {
      if (!this.isTouching) return;
      const touch     = e.touches[0];
      const deltaX    = touch.clientX - this.touchStartX;
      const deltaY    = touch.clientY - this.touchStartY;
      this.touchCurrentX = touch.clientX;

      // If scrolling vertically, abort drag
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        this.isTouching = false;
        this._resetTrack();
        return;
      }

      // Prevent vertical scroll when swiping horizontally
      e.preventDefault();

      // Live drag feedback (capped at ±30% of slide width)
      const maxDrag    = this.root.offsetWidth * 0.3;
      const clampedDX = Math.max(-maxDrag, Math.min(maxDrag, deltaX));
      const basePct    = this.current * 100;
      const dragPct    = (clampedDX / this.root.offsetWidth) * 100;
      this.track.style.transform = `translateX(calc(-${basePct}% + ${dragPct}%))`;
    }

    _onTouchEnd(e) {
      if (!this.isTouching) return;
      const deltaX = this.touchCurrentX - this.touchStartX;
      this.isTouching = false;
      this._resume();

      if (Math.abs(deltaX) >= SWIPE_THRESHOLD) {
        deltaX < 0 ? this.next() : this.prev();
      } else {
        // Snap back
        this._resetTrack();
      }
    }

    _onTouchCancel() {
      this.isTouching = false;
      this._resetTrack();
      this._resume();
    }

    _resetTrack() {
      setTransitionSpeed(this.track, this.transitionSpeed);
      this.track.style.transform = `translateX(-${this.current * 100}%)`;
    }

    // -------------------------------------------------------
    // Keyboard
    // -------------------------------------------------------
    _onKeydown(e) {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.next();
          break;
        case 'Home':
          e.preventDefault();
          this.goTo(0);
          break;
        case 'End':
          e.preventDefault();
          this.goTo(this.total - 1);
          break;
        default:
          break;
      }
    }

    // -------------------------------------------------------
    // Destroy (Shopify theme editor reload)
    // -------------------------------------------------------
    destroy() {
      this._clearTimer();
    }
  }

  // -------------------------------------------------------
  // Initialise all carousels on page
  // -------------------------------------------------------
  const carousels = new Map();

  function initAll() {
    document.querySelectorAll(SELECTOR_CAROUSEL).forEach((el) => {
      const id = el.dataset.sectionId;
      if (!carousels.has(id)) {
        carousels.set(id, new NWSCarousel(el));
      }
    });
  }

  // -------------------------------------------------------
  // Shopify theme editor events
  // -------------------------------------------------------
  document.addEventListener('DOMContentLoaded', initAll);

  // Shopify section events (live editing)
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector(SELECTOR_CAROUSEL);
    if (el) {
      const id = el.dataset.sectionId;
      if (carousels.has(id)) carousels.get(id).destroy();
      carousels.set(id, new NWSCarousel(el));
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector(SELECTOR_CAROUSEL);
    if (el) {
      const id = el.dataset.sectionId;
      if (carousels.has(id)) {
        carousels.get(id).destroy();
        carousels.delete(id);
      }
    }
  });

  document.addEventListener('shopify:block:select', (e) => {
    const carousel = e.target.closest(SELECTOR_CAROUSEL);
    if (!carousel) return;
    const instance = carousels.get(carousel.dataset.sectionId);
    if (!instance) return;
    const slides = Array.from(carousel.querySelectorAll(SELECTOR_SLIDE));
    const index  = slides.indexOf(e.target);
    if (index > -1) instance.goTo(index);
    instance._pause();
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const carousel = e.target.closest(SELECTOR_CAROUSEL);
    if (!carousel) return;
    const instance = carousels.get(carousel.dataset.sectionId);
    if (instance) instance._resume();
  });

})();
